import Constants from "@-/com.npmjs.expo-constants";
import onetime from "@-/com.npmjs.onetime";
import { LaunchArguments } from "@-/com.npmjs.react-native-launch-arguments";
import { isAdminUserEmail } from "@-/dev.improve.haniverse.lib.admin.entities";
import { useAuthSession } from "@-/dev.improve.haniverse.lib.auth.client";

/**
 * Whether unreleased, internal-only features (the quick debug panel, the Admin panel)
 * are shown: everyone on a staging build, admin accounts on any build, and an E2E run launched
 * with `hanInternalFeatures: true` (Maestro's launch-argument channel, so flows can cover internal
 * features with an ordinary seeded account) on a development bundle or a non-production variant.
 *
 * `isDevelopmentBundle` (`__DEV__`) is checked alongside the variant because a dev client's
 * variant comes from the Metro process's `EXPO_PUBLIC_APP_VARIANT`, which resolves to
 * "production" when unset.
 */
export function canAccessInternalFeatures(
  variant: unknown,
  email: string | null | undefined,
  {
    isDevelopmentBundle = false,
    launchedWithInternalFeatures = false,
  }: { isDevelopmentBundle?: boolean; launchedWithInternalFeatures?: boolean } = {},
): boolean {
  return (
    variant === "staging" ||
    variant === "alpha" ||
    isAdminUserEmail(email) ||
    (launchedWithInternalFeatures && (isDevelopmentBundle || variant !== "production"))
  );
}

/**
 * Launch arguments cannot change after launch, and reading them throws where the native module is
 * absent (web, jest), so the read is memoized and defensive.
 */
const readInternalFeaturesLaunchArgument = onetime((): boolean => {
  try {
    const value = LaunchArguments.value<{ hanInternalFeatures?: unknown }>().hanInternalFeatures;
    return value === true || value === "true" || value === "1";
  } catch {
    return false;
  }
});

export function useInternalFeatureAccess(): boolean {
  const { user } = useAuthSession();
  return canAccessInternalFeatures(Constants.expoConfig?.extra?.appVariant, user?.email, {
    isDevelopmentBundle: __DEV__,
    launchedWithInternalFeatures: readInternalFeaturesLaunchArgument(),
  });
}
